import React, { useState } from 'react';
import { observer } from 'mobx-react';
import TreeNodePrecision from '../../../models/tree/TreeNodePrecision';
import TreeStructure from '../../../models/tree/TreeStructure';
import { EnsembleThreshold } from '../../../models/ensembling';
import { Threshold } from '../../../models/ModelPerformances';
import { flattenTree } from '../../../utils';
import { LiftBar, NodeTraits, SegmentBadge } from './NodeTraits';
import { conditionTraits } from './conditionText';
import { baselineRate, liftVsBaseline } from './nodeLift';
import { nodeSegment } from './nodeSegment';
import './treeNodeTable.css';

// The tree's nodes as one row each, the third way to read a tree beside the two
// renderings of its shape. A drawn tree answers "how was the population carved
// up"; this answers "which pieces are worth anything", which needs the figures
// in columns. A list of cards can only be read node by node, never down a
// column, so nothing in it can be sorted or compared.

type SortKey =
  | 'node'
  | 'segment'
  | 'lift'
  | 'population'
  | 'conversions'
  | 'rate'
  | 'percentPopulation'
  | 'percentConversions';

type SortDirection = 'asc' | 'desc';

interface Column {
  key: SortKey | 'condition';
  label: string;
  /** The profile column is prose, so there is nothing useful to order it by. */
  sortable?: boolean;
  /** Figures are right aligned so their digits line up down the column. */
  numeric?: boolean;
  /** Where a first click on this header lands, before any toggling. */
  initial: SortDirection;
}

// Node number ascending, everything else best first: on a figure, the first
// click should show the top of the ranking, not the bottom.
const COLUMNS: Column[] = [
  { key: 'node', label: 'Node', initial: 'asc' },
  { key: 'condition', label: 'Profile', sortable: false, initial: 'asc' },
  { key: 'segment', label: 'Fit segment', initial: 'desc' },
  { key: 'lift', label: 'Lift', initial: 'desc' },
  { key: 'population', label: 'Leads', numeric: true, initial: 'desc' },
  { key: 'conversions', label: 'Conversions', numeric: true, initial: 'desc' },
  { key: 'rate', label: 'Conversion rate', numeric: true, initial: 'desc' },
  {
    key: 'percentPopulation',
    label: '% of population',
    numeric: true,
    initial: 'desc',
  },
  {
    key: 'percentConversions',
    label: '% of conversions',
    numeric: true,
    initial: 'desc',
  },
];

// Best fit highest, so sorting by segment runs the same direction as sorting by
// any of the figures next to it.
const SEGMENT_RANK: Record<Threshold, number> = {
  'very good': 4,
  good: 3,
  medium: 2,
  low: 1,
};

const ARROW: Record<SortDirection, string> = { asc: '▲', desc: '▼' };

const numberFormat = new Intl.NumberFormat('en-US');
const count = (value: number) => numberFormat.format(Math.round(value));
const percent = (value: number) => `${value.toFixed(2)}%`;

const classes = (...names: (string | false | undefined)[]) =>
  names.filter(Boolean).join(' ');

interface Row {
  key: string;
  ids: number[];
  label: string;
  /** The path condition, present only when the row is a single node. */
  condition?: string;
  /** The condition on one line, so it can be read without opening the row. */
  summary: string;
  /** A row over merged ids has no single node to open the node panel for. */
  selectable: boolean;
  expandable: boolean;
  population: number;
  conversions: number;
  conversionRate: number;
  lift: number | null;
  segment: Threshold | null;
  percentPopulation: number;
  percentConversions: number;
}

function buildRows(
  precisions: TreeNodePrecision[],
  structure: TreeStructure,
  thresholds?: EnsembleThreshold
): Row[] {
  const nodesById = new Map(
    flattenTree(structure).map((node) => [node.id, node])
  );
  const baseline = baselineRate(structure);

  return precisions.map((precision) => {
    const ids = precision.ids ?? [];
    // A row can cover several merged nodes, which then share no single
    // condition, so traits are only available when a row is one node.
    const single = ids.length === 1 ? nodesById.get(ids[0]) : undefined;
    const condition = single?.sumConditions;
    return {
      key: `node-${ids.join('-')}`,
      ids,
      label: `Node${ids.length > 1 ? 's' : ''} ${ids.join(', ')}`,
      condition,
      summary: conditionTraits(condition)
        .map((trait) => trait.text)
        .join(' · '),
      selectable: ids.length === 1,
      expandable: Boolean(condition && condition.trim()),
      population: Number(precision.population) || 0,
      conversions: Number(precision.conversions) || 0,
      conversionRate: precision.conversionRate,
      lift: liftVsBaseline(precision.conversionRate, baseline),
      segment: nodeSegment(precision.conversionRate, thresholds),
      percentPopulation: precision.percentTotalPopulation,
      percentConversions: precision.percentTotalConversion,
    };
  });
}

function sortValue(row: Row, key: SortKey): number {
  switch (key) {
    case 'node':
      return row.ids[0] ?? 0;
    case 'segment':
      return row.segment ? SEGMENT_RANK[row.segment] : 0;
    case 'lift':
      return row.lift ?? 0;
    case 'population':
      return row.population;
    case 'conversions':
      return row.conversions;
    case 'percentPopulation':
      return row.percentPopulation;
    case 'percentConversions':
      return row.percentConversions;
    case 'rate':
    default:
      return row.conversionRate;
  }
}

interface TreeNodeTableProps {
  precisions: TreeNodePrecision[];
  structure: TreeStructure;
  thresholds?: EnsembleThreshold;
  selectedNodeId?: number;
  onSelectNode: (nodeId: number) => void;
  /** The ids under the pointer, so the page can light them up elsewhere. */
  onHoverNode: (nodeIds: number[] | null) => void;
}

function TreeNodeTable({
  precisions,
  structure,
  thresholds,
  selectedNodeId,
  onSelectNode,
  onHoverNode,
}: TreeNodeTableProps) {
  // Conversion rate descending by default: the ranking the page has always
  // opened on, and the order the reach curve beside it walks the nodes in.
  const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection }>({
    key: 'rate',
    direction: 'desc',
  });
  const [expanded, setExpanded] = useState<string[]>([]);

  const toggleExpanded = (key: string) =>
    setExpanded((keys) =>
      keys.includes(key) ? keys.filter((open) => open !== key) : [...keys, key]
    );

  const sortBy = (column: Column) =>
    setSort((current) =>
      current.key === column.key
        ? {
            key: column.key as SortKey,
            direction: current.direction === 'asc' ? 'desc' : 'asc',
          }
        : { key: column.key as SortKey, direction: column.initial }
    );

  const ariaSort = (key: SortKey): React.AriaAttributes['aria-sort'] => {
    if (sort.key !== key) return 'none';
    return sort.direction === 'asc' ? 'ascending' : 'descending';
  };

  // The same guard the legend uses. With no thresholds set nothing has a
  // segment, and a column that is blank on every row is worse than no column.
  const showSegment = Boolean(nodeSegment(1, thresholds));
  const columns = showSegment
    ? COLUMNS
    : COLUMNS.filter((column) => column.key !== 'segment');

  // Deliberately not memoised: precisions and structure are MobX observables
  // mutated in place, so a dependency list would compare equal and hand back a
  // stale table. The work is a map over a few dozen nodes.
  const rows = buildRows(precisions, structure, thresholds);
  const factor = sort.direction === 'asc' ? 1 : -1;
  const sorted = [...rows].sort((a, b) => {
    const gap = (sortValue(a, sort.key) - sortValue(b, sort.key)) * factor;
    // Segments put many rows on one value, where an unspecified order would
    // reshuffle between renders. Node number settles the ties.
    return gap || a.ids[0] - b.ids[0];
  });

  return (
    <div className="tnt">
      <table className="tnt-table">
        <thead>
          <tr>
            {columns.map((column) => {
              const active = sort.key === column.key;
              if (column.sortable === false) {
                return (
                  <th key={column.key} scope="col" className="tnt-col">
                    <span className="tnt-sort tnt-sort--static">
                      {column.label}
                    </span>
                  </th>
                );
              }
              return (
                <th
                  key={column.key}
                  scope="col"
                  className={classes(
                    'tnt-col',
                    column.numeric && 'tnt-col--num'
                  )}
                  aria-sort={ariaSort(column.key as SortKey)}
                >
                  <button
                    type="button"
                    className={classes(
                      'tnt-sort',
                      active && 'tnt-sort--active'
                    )}
                    onClick={() => sortBy(column)}
                  >
                    {column.label}
                    <span className="tnt-sort-arrow" aria-hidden>
                      {active ? ARROW[sort.direction] : ''}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const isExpanded = expanded.includes(row.key);
            const isSelected = row.selectable && selectedNodeId === row.ids[0];
            const select = () => row.selectable && onSelectNode(row.ids[0]);
            const traitsLabel = `${isExpanded ? 'Hide' : 'Show'} traits for ${
              row.label
            }`;
            // Enter rather than over: the row reports once when the pointer
            // arrives instead of again for every child element it crosses. The
            // traits row carries the same pair, so opening a row and moving
            // into it does not read as leaving the node.
            const hover = {
              onMouseEnter: () => onHoverNode(row.ids),
              onMouseLeave: () => onHoverNode(null),
            };

            return (
              <React.Fragment key={row.key}>
                <tr
                  className={classes(
                    'tnt-row',
                    row.selectable && 'tnt-row--clickable',
                    isSelected && 'tnt-row--selected'
                  )}
                  tabIndex={row.selectable ? 0 : undefined}
                  aria-selected={row.selectable ? isSelected : undefined}
                  onClick={select}
                  onKeyDown={(event) => event.key === 'Enter' && select()}
                  {...hover}
                >
                  <td className="tnt-cell-node">
                    {row.expandable ? (
                      <button
                        type="button"
                        className={classes(
                          'tnt-chevron',
                          isExpanded && 'tnt-chevron--open'
                        )}
                        aria-expanded={isExpanded}
                        aria-label={traitsLabel}
                        onClick={(event) => {
                          // The row itself opens the node panel, so reading the
                          // traits in place must not also leave the page.
                          event.stopPropagation();
                          toggleExpanded(row.key);
                        }}
                      >
                        <i
                          aria-hidden
                          className="fas fa-chevron-right tnt-chevron-glyph"
                        />
                      </button>
                    ) : (
                      // Holds the column so merged rows do not sit out of line
                      // with the node names above and below them.
                      <span className="tnt-chevron tnt-chevron--empty" />
                    )}
                    <span className="tnt-node-name">{row.label}</span>
                  </td>
                  {/* Always visible and truncated. The node cards this table
                      replaced showed the first few chips on every row, so the
                      conditions could be scanned; behind a chevron alone that
                      became one click per node. */}
                  <td className="tnt-cell-condition" title={row.summary}>
                    {row.summary}
                  </td>
                  {showSegment && (
                    <td className="tnt-cell-segment">
                      <SegmentBadge
                        conversionRate={row.conversionRate}
                        thresholds={thresholds}
                      />
                    </td>
                  )}
                  <td className="tnt-cell-lift">
                    <LiftBar lift={row.lift} />
                  </td>
                  <td className="tnt-num">{count(row.population)}</td>
                  <td className="tnt-num">{count(row.conversions)}</td>
                  <td className="tnt-num">
                    {percent(row.conversionRate * 100)}
                  </td>
                  <td className="tnt-num">{percent(row.percentPopulation)}</td>
                  <td className="tnt-num">{percent(row.percentConversions)}</td>
                </tr>
                {isExpanded && (
                  <tr className="tnt-row-traits" {...hover}>
                    <td colSpan={columns.length}>
                      {/* Room for the whole path here, unlike on a node card,
                          so the chips are not collapsed after the first few. */}
                      <NodeTraits condition={row.condition} max={12} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {!sorted.length && (
            <tr>
              <td className="tnt-empty" colSpan={columns.length}>
                This tree has no scored nodes yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// Observed so the segment column appears and the rows recolour once the
// ensembling configuration arrives, which loads after the tree.
export default observer(TreeNodeTable);
